// @flow
import * as React from 'react'
import { styled } from 'mz-ui-kit'

// Types
type IconDefinition = {
  iconName: string,
  icon: Array<any>,
}
type Size = 'xs' | 'sm' | 'lg' | 'md' | string
type IconProps = {
  className?: string,
  fixedWidth?: boolean,
  innerRef?: Function,
  rotate?: string,
  size?: Size,
  alt?: string,
  'aria-hidden'?: string,
  role?: string,
}

// Constants
const SIZES = {
  xs: { fontSize: '.75em' },
  sm: { fontSize: '.875em' },
  md: { fontSize: '1em' },
  lg: {
    fontSize: '1.33333em',
    lineHeight: '0.75em',
    verticalAlign: '-.0667em',
  },
}

// Utils
const getXSize = (size) => {
  if (!size) return null
  const sizeMatch = size.match(/^([+-]?\d+(\.\d+)?)x$/)
  const style = sizeMatch && sizeMatch[1] && { fontSize: `${sizeMatch[1]}em` }
  return style || null
}

// Container component that adds a little bit styles
// for SVG element
export const Container = styled<{
  $rotate?: string,
  $fixedWidth?: boolean,
  $widthRatio: number,
  $size?: Size,
}>('svg', ({ $rotate, $fixedWidth, $widthRatio, $size }) => ({
  transform: `rotate(${$rotate || 0})`,
  display: 'inline-block',
  fontSize: 'inherit',
  height: '1em',
  width: $fixedWidth ? '1.25em' : `${0.0625 * $widthRatio}em`,
  textAlign: $fixedWidth ? 'center' : 'inherit',
  overflow: 'visible',
  verticalAlign: '-.125em',
  ...(getXSize($size) || SIZES[$size || 'md']),
}))

/**
 * Creates an icon component based on icon definition in
 * FontAwesome SVG-JS icon format.
 * @param  {Object} iconDef
 * @return {React.Component}
 */
const createIcon = (iconDef: IconDefinition) => {
  const [width, height, , , path, node] = iconDef.icon

  return class Icon extends React.PureComponent<IconProps> {
    static displayName = `Icon(${iconDef.iconName})`

    render() {
      return (
        <Container
          role={this.props.role}
          viewBox={`0 0 ${width} ${height}`}
          xmlns="http://www.w3.org/2000/svg"
          alt={this.props.alt}
          aria-hidden={this.props['aria-hidden']}
          className={this.props.className}
          ref={this.props.innerRef}
          $fixedWidth={this.props.fixedWidth}
          $size={this.props.size}
          $widthRatio={Math.ceil((width / height) * 16)}
          $rotate={this.props.rotate}
        >
          {node || <path fill="currentColor" d={path} />}
        </Container>
      )
    }
  }
}

export default createIcon
